import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_CONFIG, getNgrokHeaders } from '../../config/api';

// Define the base URL for your API
const API_BASE_URL = API_CONFIG.BASE_URL;

// Create the base query with authentication
const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    // Get the token from the Redux state
    const token = getState().auth?.token;
    
    // Set ngrok headers to skip browser warning
    const ngrokHeaders = getNgrokHeaders();
    Object.entries(ngrokHeaders).forEach(([key, value]) => {
      headers.set(key, value);
    });
    
    // If we have a token, set the authorization header
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    
    return headers;
  },
});

// Create the API slice
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery,
  tagTypes: ['Booking', 'Price', 'User', 'Stats'],
  endpoints: (builder) => ({
    // Authentication endpoints
    login: builder.mutation({
      query: (credentials) => ({
        url: '/admin/login',
        method: 'POST',
        body: {
          username: credentials.username,
          password: credentials.password,
        },
      }),
      invalidatesTags: ['User'],
    }),
    
    logout: builder.mutation({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
      invalidatesTags: ['User'],
    }),
    
    refreshToken: builder.mutation({
      query: () => ({
        url: '/auth/refresh',
        method: 'POST',
      }),
    }),
    
    // User endpoints
    getProfile: builder.query({
      query: () => '/auth/profile',
      providesTags: ['User'],
    }),
    
    updateProfile: builder.mutation({
      query: (profileData) => ({
        url: '/auth/profile',
        method: 'PUT',
        body: profileData,
      }),
      invalidatesTags: ['User'],
    }),
    
    // Booking endpoints
    getBookings: builder.query({
      query: ({ page = 1, limit = 10, status, search, dateRange } = {}) => {
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('limit', limit);
        if (status && status !== 'all') params.append('status', status);
        if (search) params.append('search', search);
        if (dateRange?.start) params.append('startDate', dateRange.start);
        if (dateRange?.end) params.append('endDate', dateRange.end);
        
        return `/bookings?${params.toString()}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Booking', id })),
              { type: 'Booking', id: 'LIST' },
            ]
          : [{ type: 'Booking', id: 'LIST' }],
    }),
    
    getBooking: builder.query({
      query: (id) => `/bookings/${id}`,
      providesTags: (result, error, id) => [{ type: 'Booking', id }],
    }),
    
    createBooking: builder.mutation({
      query: (bookingData) => ({
        url: '/bookings',
        method: 'POST',
        body: bookingData,
      }),
      invalidatesTags: [{ type: 'Booking', id: 'LIST' }, 'Stats'],
    }),
    
    updateBooking: builder.mutation({
      query: ({ id, ...bookingData }) => ({
        url: `/bookings/${id}`,
        method: 'PUT',
        body: bookingData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Booking', id },
        { type: 'Booking', id: 'LIST' },
        'Stats',
      ],
    }),
    
    updateBookingStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/bookings/${id}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Booking', id },
        { type: 'Booking', id: 'LIST' },
        'Stats',
      ],
    }),
    
    deleteBooking: builder.mutation({
      query: (id) => ({
        url: `/bookings/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Booking', id: 'LIST' }, 'Stats'],
    }),
    
    // Price endpoints
    getPrices: builder.query({
      query: ({ page = 1, limit = 10, category, search } = {}) => {
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('limit', limit);
        if (category && category !== 'all') params.append('category', category);
        if (search) params.append('search', search);
        
        return `/prices?${params.toString()}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Price', id })),
              { type: 'Price', id: 'LIST' },
            ]
          : [{ type: 'Price', id: 'LIST' }],
    }),
    
    getPrice: builder.query({
      query: (id) => `/prices/${id}`,
      providesTags: (result, error, id) => [{ type: 'Price', id }],
    }),
    
    createPrice: builder.mutation({
      query: (priceData) => ({
        url: '/prices',
        method: 'POST',
        body: priceData,
      }),
      invalidatesTags: [{ type: 'Price', id: 'LIST' }],
    }),
    
    updatePrice: builder.mutation({
      query: ({ id, ...priceData }) => ({
        url: `/prices/${id}`,
        method: 'PUT',
        body: priceData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Price', id },
        { type: 'Price', id: 'LIST' },
      ],
    }),
    
    deletePrice: builder.mutation({
      query: (id) => ({
        url: `/prices/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Price', id: 'LIST' }],
    }),
    
    bulkUpdatePrices: builder.mutation({
      query: (priceUpdates) => ({
        url: '/prices/bulk-update',
        method: 'PUT',
        body: { updates: priceUpdates },
      }),
      invalidatesTags: [{ type: 'Price', id: 'LIST' }],
    }),
    
    getPriceHistory: builder.query({
      query: (id) => `/prices/${id}/history`,
      providesTags: (result, error, id) => [{ type: 'Price', id: `${id}-history` }],
    }),
    
    exportPrices: builder.mutation({
      query: (format = 'csv') => ({
        url: `/prices/export?format=${format}`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),
    
    importPrices: builder.mutation({
      query: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        return {
          url: '/prices/import',
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: [{ type: 'Price', id: 'LIST' }],
    }),
    
    // Statistics endpoints
    getStats: builder.query({
      query: () => '/stats',
      providesTags: ['Stats'],
    }),
    
    getBookingStats: builder.query({
      query: ({ period = 'month' } = {}) => `/stats/bookings?period=${period}`,
      providesTags: ['Stats'],
    }),
    
    getPriceStats: builder.query({
      query: () => '/stats/prices',
      providesTags: ['Stats'],
    }),
    
    // Dashboard endpoints
    getDashboardData: builder.query({
      query: () => '/dashboard',
      providesTags: ['Stats', 'Booking', 'Price'],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  // Auth hooks
  useLoginMutation,
  useLogoutMutation,
  useRefreshTokenMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
  
  // Booking hooks
  useGetBookingsQuery,
  useGetBookingQuery,
  useCreateBookingMutation,
  useUpdateBookingMutation,
  useUpdateBookingStatusMutation,
  useDeleteBookingMutation,
  
  // Price hooks
  useGetPricesQuery,
  useGetPriceQuery,
  useCreatePriceMutation,
  useUpdatePriceMutation,
  useDeletePriceMutation,
  useBulkUpdatePricesMutation,
  useGetPriceHistoryQuery,
  useExportPricesMutation,
  useImportPricesMutation,
  
  // Stats hooks
  useGetStatsQuery,
  useGetBookingStatsQuery,
  useGetPriceStatsQuery,
  useGetDashboardDataQuery,
} = apiSlice;
